import { apolloClient } from '@client/api/graphql/client';
import { InfiniteData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Author,
  GetCommunityPostDocument,
  GetCommunityPostQueryVariables,
  GetCommunityPostQuery,
  Post,
  Reply,
} from '../../../../generated/graphql';
import { communityQueryKeys } from '../constants';

type AuthorWithOptionalMarketing = Omit<Author, 'marketing'> &
  Partial<Pick<Author, 'marketing'>>;

type ReplyWithOptionalMarketing = Omit<Reply, 'author'> & {
  author: AuthorWithOptionalMarketing;
};

type PostWithOptionalMarketing = Omit<Post, 'author' | 'replies'> & {
  author: AuthorWithOptionalMarketing;
  replies?: ReplyWithOptionalMarketing[] | null;
};

const withMarketing = (author: AuthorWithOptionalMarketing): Author => ({
  ...author,
  marketing: author.marketing ?? false,
});

const mapReplies = (replies?: ReplyWithOptionalMarketing[] | null): Reply[] =>
  (replies || []).map(
    (reply) =>
      ({
        ...reply,
        author: withMarketing(reply.author),
      } as Reply),
  );

const mapPostMarketing = (
  post?: PostWithOptionalMarketing | null,
): Post | null =>
  post
    ? ({
        ...post,
        author: withMarketing(post.author),
        replies: mapReplies(post.replies),
      } as Post)
    : null;

export const useCommunityPost = (communityId: number, postId: string) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: communityQueryKeys.post(communityId, postId),
    queryFn: async () => {
      const response = await apolloClient.query<
        GetCommunityPostQuery,
        GetCommunityPostQueryVariables
      >({
        query: GetCommunityPostDocument,
        variables: {
          communityId,
          postId,
        },
        fetchPolicy: 'network-only',
      });

      return response.data.post ? mapPostMarketing(response.data.post) : null;
    },
    keepPreviousData: true,
    refetchOnMount: true,
    initialData: () => {
      const previousPosts = queryClient.getQueryData<InfiniteData<Post[]>>(
        communityQueryKeys.posts(communityId),
      );
      if (previousPosts) {
        for (const page of previousPosts.pages) {
          const foundPost = page.find((post) => post.id === postId);
          if (foundPost) return mapPostMarketing(foundPost);
        }
      }
    },
    enabled: typeof communityId !== null && typeof postId !== null,
  });
};
